/**
 * Shared date/time formatters — DD/MM/YYYY in both EN and AR per I18N-9.6.
 *
 * Phase 7 finding (live-deploy probe): the four admin pages
 * (QueueHealth, ClockSkew, OutdatedApk, WorkerList) each had their own
 * `new Date(iso).toLocaleString()` call with no explicit options. On
 * Windows en-US that produced "5/2/2026, 5:05:32 AM" (M/D/YYYY) which
 * violates the I18N-9.6 spec contract that BOTH locales render dates as
 * DD/MM/YYYY (so PM/operators can read either locale without ambiguity
 * about which number is the month).
 *
 * Use these helpers everywhere instead of bare toLocaleString. The XLSX
 * exports already emit DD/MM/YYYY HH:mm via the server-side formatter;
 * this brings the web UI in line.
 *
 * Latin digits in both locales (I18N-9.6) — uses the `en-GB` locale tag
 * which renders DD/MM/YYYY with Latin digits regardless of the user's
 * app-locale; the HTML `dir` attribute drives RTL layout independently.
 */

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

/**
 * Format ISO timestamp as DD/MM/YYYY HH:mm in 24-hour time.
 * Returns "—" for null/empty input. Returns the raw input on parse failure.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", DATETIME_OPTS);
  } catch {
    return iso;
  }
}

/**
 * Format ISO timestamp as DD/MM/YYYY (no time component).
 * Returns "—" for null/empty input.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", DATE_OPTS);
  } catch {
    return "—";
  }
}
